import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {FooterComponent} from '../shared/components/footer/footer.component';
import {IndexedDbAnswersService} from '../shared/services/indexeddb-answers.service';
import {IndexedDbUnitService} from '../shared/services/indexeddb-unit.service';

import {Guid} from 'guid-typescript';
import {catchError, forkJoin, lastValueFrom, of, Subject, take, takeUntil,} from 'rxjs';

import {DeviceService} from '../shared/services/device.service';
import {ImageService} from '../shared/services/img-base64.service';
import {IndexedDbPollService} from '../shared/services/indexeddb-poll.service';
import {PollService} from '../shared/services/poll.service';
import {UnitService} from '../shared/services/unit.service';
import {IncentiveService} from '../shared/services/incentive.service';
import {IndexedDbIncentiveService} from '../shared/services/indexeddb-incentive.service';
import {DeviceBlockStateService, IDeviceBlockState,} from '../shared/services/device-block-state.service';
import {IPoll} from 'projects/lib-ui/src/shared/interfaces/poll';
import {PollManagerComponent} from 'projects/lib-ui/src/shared/components/poll-manager/poll-manager.component';
import {
  PageUnavailableComponent
} from 'projects/lib-ui/src/shared/components/page-unavailable/page-unavailable.component';
import {IPartialUnit} from 'projects/lib-ui/src/shared/interfaces/unit';
import {IIncentive} from 'projects/lib-ui/src/shared/interfaces/incentive';
import {IPollResponse, IResponses,} from 'projects/lib-ui/src/shared/interfaces/poll-responses';
import {PollTypeEnum} from 'projects/sapore-survey-pwa/src/app/shared/enums/poll-type.enum';
import {PollAlertOverlayComponent} from './components/poll-alert-overlay/poll-alert-overlay.component';
import {PollBlockScreenComponent} from './components/poll-block-screen/poll-block-screen.component';
import {POLL_UI_STATE, PollUiState} from './poll-ui-state.type';

@Component({
  selector: 'app-poll',
  standalone: true,
  imports: [
    CommonModule,
    PollManagerComponent,
    FooterComponent,
    PageUnavailableComponent,
    PollAlertOverlayComponent,
    PollBlockScreenComponent,
  ],
  providers: [UnitService, DeviceService],
  templateUrl: './poll.component.html',
  styleUrls: ['./poll.component.scss'],
})
export class PollComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly indexedDbAnswersService = inject(IndexedDbAnswersService);
  readonly indexedDbUnitService = inject(IndexedDbUnitService);
  private readonly unitService = inject(UnitService);
  private readonly pollService = inject(PollService);
  private readonly imageService = inject(ImageService);
  private readonly indexedDbPollService = inject(IndexedDbPollService);
  private readonly deviceService = inject(DeviceService);
  private readonly incentiveService = inject(IncentiveService);
  private readonly indexedDbIncentiveService = inject(
    IndexedDbIncentiveService,
  );
  private readonly deviceBlockStateService = inject(DeviceBlockStateService);

  private readonly unsubscribe$ = new Subject();

  poll!: IPoll;
  unitId!: string;
  deviceId!: string;

  unit!: IPartialUnit;

  incentive: IIncentive | null = null;
  pollIsLoadingDone: boolean = false;

  interval: ReturnType<typeof setInterval> | null = null;
  intervalOneHour: ReturnType<typeof setInterval> | null = null;
  intervalQuarterHour: ReturnType<typeof setInterval> | null = null;
  intervalOneDay: ReturnType<typeof setInterval> | null = null;

  lockScreen = false;
  lockScreenWithoutDevice = false;

  readonly uiStates = POLL_UI_STATE;
  uiState: PollUiState = this.uiStates.NORMAL;

  get activeAlertLevel(): number | null {
    if (this.uiState === this.uiStates.ALERT_LEVEL_1) {
      return 1;
    }

    if (this.uiState === this.uiStates.ALERT_LEVEL_2) {
      return 2;
    }

    return null;
  }

  pinValidationInProgress = false;
  pinValidationError = '';
  resendCodeLoading = false;
  resendCooldownSeconds = 60;

  private deviceBlockStateTimer: ReturnType<typeof setTimeout> | null = null;
  private resendCooldownTimer: ReturnType<typeof setInterval> | null = null;
  private unlockSuccessTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.poll = this.activatedRoute.snapshot.data['poll'];
    this.unitId = this.activatedRoute.snapshot.paramMap.get('unitId')!;
    this.deviceId = this.activatedRoute.snapshot.paramMap.get('deviceId')!;

    if (!this.incentive) {
      this.incentiveGetIndexedDb().then(() => {
      });
    }

    this.interval = setInterval(() => {
      this.handlerSchedule();
    }, 15000);

    this.intervalOneHour = setInterval(() => {
      if (!navigator.onLine) return;
      this.getUnit();
    }, 3600000);

    this.intervalQuarterHour = setInterval(() => {
      if (!navigator.onLine) return;
      this.checkDeviceExists();
    }, 900000);

    this.intervalOneDay = setInterval(() => {
      if (!navigator.onLine) return;
      this.getIncentive().then(() => {
      });
    }, 86400000);

    this.getUnitFromIndexdDB();

    this.deviceBlockStateService.blockState$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.applyBlockState(state);
      });
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.intervalOneHour) {
      clearInterval(this.intervalOneHour);
      clearInterval(this.intervalOneHour);
      this.intervalOneHour = null;
    }
    if (this.intervalQuarterHour) {
      clearInterval(this.intervalQuarterHour);
      this.intervalQuarterHour = null;
    }
    if (this.intervalOneDay) {
      clearInterval(this.intervalOneDay);
      this.intervalOneDay = null;
    }
    if (this.deviceBlockStateTimer) {
      clearTimeout(this.deviceBlockStateTimer);
      this.deviceBlockStateTimer = null;
    }
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }
    if (this.unlockSuccessTimeout) {
      clearTimeout(this.unlockSuccessTimeout);
      this.unlockSuccessTimeout = null;
    }

    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  checkDeviceExists() {
    this.deviceService
      .checkDeviceExists(this.unit.device.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.lockScreenWithoutDevice = false;
            this.handlerSchedule();
          } else {
            this.lockScreenWithoutDevice = true;
          }
        },
      });
  }

  handlerSchedule(): void {
    let lockScreenItem = true;

    this.unit.availableShifts.forEach((shift) => {
      if (shift.typePoll !== this.poll.type) {
        lockScreenItem = false;
        return;
      }

      const now = new Date();

      const [initialHoras, initialMinutos] = shift.startTime
        .split(':')
        .map(Number);

      const [endHoras, endMinutos] = shift.endTime.split(':').map(Number);

      const start = new Date(
        new Date().setHours(initialHoras, initialMinutos, 0, 0),
      );
      const end = new Date(new Date().setHours(endHoras, endMinutos, 0, 0));

      const crossesMidnight = end < start;

      if (crossesMidnight) {
        if (now >= start || now <= end) {
          lockScreenItem = false;
        }
      } else {
        if (now >= start && now <= end) {
          lockScreenItem = false;
        }
      }
    });

    if (!this.unit.availableShifts.length) {
      lockScreenItem = false;
    }

    this.lockScreen = lockScreenItem;
  }

  getUnitFromIndexdDB(): void {
    this.indexedDbUnitService
      .getUnitById(this.unitId)
      .then((unit) => {
        this.unit = unit;
        this.bootstrapBlockStatus();
        this.checkDeviceExists();
      });
  }

  private bootstrapBlockStatus(): void {
    const deviceId = this.unit?.device?.id;
    if (!deviceId || !navigator.onLine) {
      return;
    }

    this.pollService
      .getBlockStatus(deviceId)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data?.blockState) {
            return;
          }

          this.deviceBlockStateService.setBlockState(
            response.data.blockState,
            'bootstrap',
          );
        },
      });
  }

  async getUnit(): Promise<void> {
    const res = await lastValueFrom(
      this.unitService.getUnits(this.unit.code).pipe(take(1)),
    );
    if (!res || !res.success || !res.data || res.data.length === 0) {
      console.error('Nenhuma unidade encontrada com o código:', this.unit.code);
      return;
    }
    const unitRes = res.data[0];

    if (unitRes.pollId || unitRes.pollDishReservationId) {
      const data: IPartialUnit = {
        ...unitRes,
        device: this.unit.device,
      };

      this.addUnitToIndexedDb(data);
    }
  }

  addUnitToIndexedDb(data: IPartialUnit): void {
    this.indexedDbUnitService.addUnit(data).then(() => {
      this.getPoll(data);
      this.getUnitFromIndexdDB();
    });
  }

  async getPoll(dataUnit: IPartialUnit): Promise<void> {
    try {
      const pollId =
        dataUnit.device.typePoll === PollTypeEnum.Satisfaction
          ? dataUnit.pollId
          : dataUnit.pollDishReservationId;
      const response = await lastValueFrom(
        this.pollService.getPollById(pollId).pipe(take(1)),
      );

      if (response.success && response.data) {
        this.imageService.convertImgsURLToBase64(response.data).then((res) => {
          this.indexedDbPollService.addPoll(res).then(() => {
            this.poll = res;
          });
        });
      }
    } catch (error) {
      console.error('Erro ao buscar a pesquisa:', error);
    }
  }

  async onFinishPoll(responses: IResponses[]): Promise<void> {
    const answers: IPollResponse = {
      id: Guid.create().toString(),
      branchId: this.unitId,
      pollId: this.poll.id,
      deviceId: this.unit.device.id,
      createdAt: new Date(
        new Date().getTime() - 3 * 60 * 60 * 1000,
      ).toISOString(),
      pollAnswers: responses,
    };

    this.indexedDbAnswersService
      .addAnswers(answers)
      .then(() => 'Answers added');
  }

  onValidatePin(pin: string): void {
    const deviceId = this.unit?.device?.id;
    if (!deviceId || this.pinValidationInProgress) {
      return;
    }

    this.pinValidationInProgress = true;
    this.pinValidationError = '';

    this.pollService
      .validatePin(deviceId, pin)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.pinValidationInProgress = false;
          if (response.success && response.data?.unlocked) {
            this.deviceBlockStateService.clearBlockState();
            this.showUnlockSuccess();
            return;
          }

          this.pinValidationError = 'Codigo invalido. Tente novamente.';
        },
        error: () => {
          this.pinValidationInProgress = false;
          this.pinValidationError = 'Falha ao validar codigo. Tente novamente.';
        },
      });
  }

  onResendCode(): void {
    const deviceId = this.unit?.device?.id;
    if (!deviceId || this.resendCodeLoading || this.resendCooldownSeconds > 0) {
      return;
    }

    const startedWhileBlockedLevelThree =
      this.uiState === this.uiStates.BLOCKED_LEVEL_3;

    if (!startedWhileBlockedLevelThree) {
      return;
    }

    this.resendCodeLoading = true;

    this.pollService
      .resendCode(deviceId)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.resendCodeLoading = false;
          if (this.uiState === this.uiStates.BLOCKED_LEVEL_3) {
            this.startResendCooldown();
          }

          const remainingSeconds = response.data?.remainingSeconds;
          if (
            response.success &&
            isFinite(Number(remainingSeconds)) &&
            remainingSeconds !== undefined &&
            this.uiState === this.uiStates.BLOCKED_LEVEL_3
          ) {
            this.deviceBlockStateService.setBlockState(
              {
                alertLevel: 3,
                remainingSeconds,
              },
              'resend-code',
            );
          }
        },
        error: () => {
          this.resendCodeLoading = false;
          if (this.uiState === this.uiStates.BLOCKED_LEVEL_3) {
            this.startResendCooldown();
          }
        },
      });
  }

  private applyBlockState(state: IDeviceBlockState | null): void {
    if (!state || state.alertLevel === null) {
      if (this.uiState !== this.uiStates.UNLOCK_SUCCESS) {
        this.uiState = this.uiStates.NORMAL;
      }
      this.clearBlockTimer();
      return;
    }

    const wasBlockedLevelThree = this.uiState === this.uiStates.BLOCKED_LEVEL_3;
    const nextUiState = this.resolveUiStateByAlertLevel(state.alertLevel);

    this.pinValidationError = '';
    if (nextUiState === this.uiStates.BLOCKED_LEVEL_3) {
      this.uiState = this.uiStates.BLOCKED_LEVEL_3;
      if (!wasBlockedLevelThree) {
        this.startResendCooldown();
      }
    } else {
      this.uiState = nextUiState;
    }

    this.startOrReplaceBlockTimer(state.expiresAt);
  }

  private resolveUiStateByAlertLevel(
    alertLevel: Exclude<IDeviceBlockState['alertLevel'], null>,
  ): PollUiState {
    if (alertLevel === 3) {
      return this.uiStates.BLOCKED_LEVEL_3;
    }

    if (alertLevel === 1) {
      return this.uiStates.ALERT_LEVEL_1;
    }

    if (alertLevel === 2) {
      return this.uiStates.ALERT_LEVEL_2;
    }

    return this.uiStates.ALERT_LEVEL_1;
  }

  private startOrReplaceBlockTimer(expiresAt: number): void {
    this.clearBlockTimer();
    const timeoutMs = Math.max(0, expiresAt - Date.now());

    this.deviceBlockStateTimer = setTimeout(() => {
      const wasBlockedLevelThree =
        this.uiState === this.uiStates.BLOCKED_LEVEL_3;
      if (!wasBlockedLevelThree && this.uiState !== this.uiStates.UNLOCK_SUCCESS) {
        this.uiState = this.uiStates.NORMAL;
      }
      this.deviceBlockStateService.clearBlockState();
      if (wasBlockedLevelThree) {
        this.showUnlockSuccess();
      }
    }, timeoutMs);
  }

  private clearBlockTimer(): void {
    if (this.deviceBlockStateTimer) {
      clearTimeout(this.deviceBlockStateTimer);
      this.deviceBlockStateTimer = null;
    }
  }

  private showUnlockSuccess(): void {
    this.uiState = this.uiStates.UNLOCK_SUCCESS;
    if (this.unlockSuccessTimeout) {
      clearTimeout(this.unlockSuccessTimeout);
    }

    this.unlockSuccessTimeout = setTimeout(() => {
      if (this.uiState === this.uiStates.UNLOCK_SUCCESS) {
        this.uiState = this.uiStates.NORMAL;
      }
    }, 2000);
  }

  private startResendCooldown(): void {
    this.resendCooldownSeconds = 60;
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
    }

    this.resendCooldownTimer = setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0 && this.resendCooldownTimer) {
        clearInterval(this.resendCooldownTimer);
        this.resendCooldownTimer = null;
      }
    }, 1000);
  }

  private async getIncentive(): Promise<void> {
    if (!this.unitId) return;
    const incentive = await lastValueFrom(
      this.incentiveService.getBranchById(this.unitId).pipe(take(1)),
    );

    this.incentive = null;
    this.indexedDbIncentiveService
      .clearDB()
      .catch((error) => {
        console.error('Error deleting incentive from IndexedDB:', error);
      });

    if (!incentive) {
      return;
    }

    const todayDate = new Date().toISOString().split('T')[0];
    const startDate = incentive.startDate.split('T')[0];
    const endDate = incentive.endDate.split('T')[0];

    if (startDate <= todayDate && endDate > todayDate) {
      forkJoin({
        noticePageImg: this.imageService
          .convertToBase64(incentive.noticePageImg)
          .pipe(catchError(() => of('' as string))),
        winnerPageImg: this.imageService
          .convertToBase64(incentive.winnerPageImg)
          .pipe(catchError(() => of('' as string))),
      }).subscribe({
        next: ({noticePageImg, winnerPageImg}) => {
          this.incentive = {
            ...incentive,
            noticePageImg,
            winnerPageImg,
          };

          if (this.incentive) {
            this.incentiveAddIndexedDb(this.incentive);
          }
        },
      });
    }
  }

  private incentiveAddIndexedDb(incentive: IIncentive): void {
    if (!this.incentive) return;

    this.indexedDbIncentiveService
      .add(incentive)
      .then(() => {
      })
      .catch((error) => {
        console.error('Error adding incentive to IndexedDB:', error);
      });
  }

  private async incentiveGetIndexedDb(): Promise<void> {
    this.indexedDbIncentiveService
      .get()
      .then((incentive) => {
        this.incentive = incentive;
        this.pollIsLoadingDone = true;
      })
      .catch((error) => {
        console.error('Error getting incentive from IndexedDB:', error);
        this.pollIsLoadingDone = true;
      });
  }
}
